import {
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ChangeDetectorRef,
    AfterViewInit,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr';
import * as Feather from 'feather-icons';
import { ToastrService } from 'ngx-toastr';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { formatLocalDateKey, lcStationId, readObservableError, todayLocalDateKey } from 'app/services/logiciel-caisse/logiciel-caisse-api.helper';
import { LogicielCaisseTicketService } from 'app/services/logiciel-caisse/logiciel-caisse-ticket.service';
import { LogicielCaisseEntrepotService } from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';
import {
    LogicielCaisseParametreAssociationsService,
    EtablissementRow,
} from 'app/services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import { LogicielCaisseTicketSelectionService } from 'app/services/logiciel-caisse/logiciel-caisse-ticket-selection.service';
import {
    LogicielCaisseCaisseService,
    CaisseTableRow,
} from 'app/services/logiciel-caisse/logiciel-caisse-caisse.service';
import { LogicielCaisseTicketFiltersService,
    TicketListFiltersSession,
    TicketTotalFilterOperator,
} from 'app/services/logiciel-caisse/logiciel-caisse-ticket-filters.service';
import { DocumentListLoadingService } from 'app/services/document-list-loading.service';
import {
    mapApiDetailToPosDetail,
    mapApiDetailResponseToPosDetail,
    mapApiTicketRowToPos,
    mapPosTabToApiStatut,
    formatDocumentTypeLabel,
} from 'app/services/logiciel-caisse/logiciel-caisse-ticket.mapper';
import { ConvertDocumentTarget } from '../convert-tick-caiss/convert-tick-caiss.component';

export type PosPaymentStatus = 'paye' | 'non_paye' | 'partiel';

export type PosTicketAvailability =
    | 'ticke'
    | 'en_traitement'
    | 'consolide'
    | 'facture_client'
    | 'avoire'
    | 'annule';

export interface PosTicketRow {
    id: string;
    numero: string;
    date: string;
    clientName: string;
    clientPhone: string;
    entrepot: string;
    entrepotId: string | null;
    paymentStatus: PosPaymentStatus;
    paymentType: string;
    /** Montant TTC du ticket (liste). */
    ticketTotal?: number;
    ticketStatus: PosTicketAvailability;
    /** Statut document brut (en_cours, termine, annulé). */
    documentStatutRaw?: string | null;
    /** Libellé affiché du statut document. */
    documentStatutLabel?: string;
    factureDocumentId?: number | null;
    documentOriginal?: string | null;
    /** ID document source (document_original_id). */
    documentOriginalId?: number | null;
    /** ticket_vente | ticket_en_traitement | facture (API _documentType). */
    documentType?: string | null;
    /** Libellé type affiché (Tické, Disponible, Facture, Avoiré). */
    documentTypeLabel?: string;
    /** Famille API : ticke | initialisation */
    famille?: string | null;
    /** documents.sous_type (ex. acompte = Réservation avec paiement). */
    sousType?: string | null;
    /** Libellé sous-type affiché. */
    sousTypeLabel?: string | null;
    /** Clôture Initialisation : non_cloture | cloture */
    cloture?: string | null;
    clotureLabel?: string | null;
    /** documents.consolide = 1 */
    isConsolide?: boolean;
    /** Ticket source ayant fait l'objet d'un avoir compensatoire. */
    hasAvoireEmis?: boolean;
    /** En traitement converti en ticket_vente. */
    hasVenteEmis?: boolean;
    /** En traitement annulé (ticket_annule émis). */
    hasAnnulationEmis?: boolean;
    /** Modes de paiement (liste + détail). */
    paymentModes?: PosPaymentMode[];
    /** Libellés modes agrégés pour la colonne liste (ex. « Espèces, Carte »). */
    paymentModesLabel?: string;
    /** documents.origine : web | store → Web / Physique. */
    origine?: string | null;
    origineLabel?: string | null;
}

/** Ligne article affichée dans le panneau détail (données API ligne_document). */
export interface PosTicketLine {
    reference: string;
    couleur: string;
    taille: string;
    colorSize: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
}

/** Mode de règlement agrégé (Espèces, Carte bancaire, Chèque…). */
export interface PosPaymentMode {
    label: string;
    amount: number;
    logoUrl?: string | null;
}

/** Vue détaillée du ticket pour le panneau latéral. */
export interface PosTicketDetail {
    ticket: PosTicketRow;
    lines: PosTicketLine[];
    totalPrice: number;
    amountPaid: number;
    resteAPayer?: number;
    /** Modes de paiement utilisés (mode_paiement / type_mode_paiment). */
    paymentModes?: PosPaymentMode[];
}

interface WarehouseOption {
    id: string;
    label: string;
}

@Component({
    selector: 'app-poscontent',
    templateUrl: './poscontent.component.html',
    styleUrls: ['./poscontent.component.scss']
})
export class POSContentComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('selectAllCheckbox') selectAllCheckbox?: ElementRef<HTMLInputElement>;
    @ViewChild('datePicker') datePicker!: ElementRef;
    @ViewChild('dateJusquaAchat') dateJusquaAchat!: ElementRef;
    @ViewChild('dateDuVente') dateDuVente!: ElementRef;
    @ViewChild('dateJusquaVente') dateJusquaVente!: ElementRef;
    /** Statut d’onglet parent (ex. Tous, Tické, Disponible) — utilisé pour filtrer la liste. */
    @Input() status = 'Tous';
    /** Active les mêmes actions / modal détail que l’onglet Consolidé (utilisé par Tické). */
    @Input() consolideLikeActions = false;
    /** tickets = tickets caisse ; negoce = factures / avoirs négoce. */
    @Input() listContext: 'tickets' | 'negoce' = 'tickets';
    isLoadingExpanded = false;
    viewMode: 'jours' | 'mois' | 'annees' = 'jours';
    weekDays: { label: string; dateString: string; isToday: boolean }[] = [];
    currentMonthIndex = new Date().getMonth();
    currentYear = new Date().getFullYear();
    selectedMonthIndex: number | null = null;
    selectedDay: string = '';
    selectedYear: number | null = null;
    selectedDate: Date | null = null;
    selectedPeriod = '';
    dateFilterStart: string = '';
    dateFilterEnd: string = '';
    months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    years: number[] = [];
    filters: any = {
        raison: '',
        matricule: '',
        chiffre_aff: '',
        operator: '=',
        client_id: null,
        fournisseur_id: null,
        projet_id: null,
        date_du: '',
        date_jusqua: '',
        selectedStatuses: []
    };

    /** Aligné sur shared.component */
    searchTerm = '';
    selectedWarehouseIds: string[] = [];
    selectedPaymentTypes: string[] = [];
    selectedTicketTypes: string[] = [];
    showFilter = false;

    filteredData: PosTicketRow[] = [];
    paginatedData: PosTicketRow[] = [];
    basicSelectedOption = 10;
    totalPages = 1;
    pages: number[] = [];

    get ticketTableColspan(): number {
        return this.showClotureColumn ? 13 : 12;
    }

    warehouseOptions: WarehouseOption[] = [];

    /** Filtre établissement (barre d'outils POS). */
    establishmentOptions: { id: string; label: string }[] = [];
    selectedEstablishmentIds: string[] = [];
    /** Map établissement_id -> entrepot_ids (pour restreindre le filtrage). */
    private entrepotIdsByEtablissement = new Map<string, string[]>();

    paymentTypeOptions = ['Payé', 'En attente', 'Partiellement payé'];

    readonly ticketTypeFilterOptions = [
        { value: 'vente', label: 'Vente' },
        { value: 'acompte', label: 'Réservation avec paiement' },
        { value: 'echange', label: 'Échange' },
        { value: 'avoir', label: 'Avoir' },
    ];

    get showTicketTypeFilter(): boolean {
        const tab = this.normalizeTabKey();
        return this.listContext === 'tickets' && (tab === 'ticke' || tab === 'consolide');
    }

    readonly ticketStatutFilterOptions = [
        { value: 'en_cours', label: 'En cours' },
        { value: 'termine', label: 'Terminé' },
    ];
    selectedTicketStatuts: string[] = [];

    totalFilterOperator: TicketTotalFilterOperator | '' = '';
    totalFilterValue: number | null = null;
    totalFilterValueMax: number | null = null;

    readonly totalFilterOperatorOptions: { value: TicketTotalFilterOperator; label: string }[] = [
        { value: '=', label: '=' },
        { value: '>', label: '>' },
        { value: '<', label: '<' },
        { value: 'between', label: 'Entre' },
    ];

    /** Données tickets chargées depuis l’API logiciel de caisse. */
    allTickets: PosTicketRow[] = [];

    isLoadingTickets = false;
    hasDataLoaded = false;
    readonly skeletonRows = [0, 1, 2, 3, 4];
    detailLoading = false;
    serverTotalPages = 1;
    serverTotal = 0;

    selectedIds = new Set<string>();

    currentPage = 1;

    /** Panneau latéral « détails ticket de caisse » */
    ticketDetail: PosTicketDetail | null = null;

    /** Panneau « ticket de caisse origine » (à côté du détail principal). */
    originTicketDetail: PosTicketDetail | null = null;
    originDetailLoading = false;

    /** Transfert ticket → facture en cours */
    transferInProgress = false;
    transferLoadingMessage = 'Transfert vers facture en cours…';

    /** Modale conversion ticket → document (app-convert-tick-caiss) */
    showConvertModal = false;
    convertModalDefaultType: string | null = null;
    ticketToConvert: PosTicketRow | null = null;

    /** Modale annulation ticket (compensatoire, pas suppression définitive). */
    showAnnulerModal = false;
    annulerModalTicketIds: number[] = [];
    cancelSubmitting = false;
    isAdmin = true;

    /** Passation ticket ERP → caisse physique (WebSocket). */
    showPassationModal = false;
    passationTicket: PosTicketRow | null = null;
    passationCaisses: CaisseTableRow[] = [];
    selectedPassationCaisseId: string | null = null;
    passationSubmitting = false;
    passationCaissesLoading = false;
    passationAmountPaid: number | null = null;
    clotureSubmitting = false;

    private flatpickrInstance: flatpickr.Instance | null = null;
    private ticketsLoadSub: Subscription | null = null;
    private detailLoadSub: Subscription | null = null;
    private originDetailLoadSub: Subscription | null = null;

    constructor(
        private cdr: ChangeDetectorRef,
        private authService: LogicielCaisseAuthService,
        private ticketService: LogicielCaisseTicketService,
        private entrepotService: LogicielCaisseEntrepotService,
        private associationsService: LogicielCaisseParametreAssociationsService,
        private selectionService: LogicielCaisseTicketSelectionService,
        private ticketFiltersService: LogicielCaisseTicketFiltersService,
        private caisseService: LogicielCaisseCaisseService,
        private toastr: ToastrService,
        private router: Router,
        public listLoading: DocumentListLoadingService
    ) {}

    get isTableLoading(): boolean {
        return this.isLoadingTickets || this.listLoading.isLoading;
    }

    ngOnInit(): void {
        this.years = Array.from({ length: 10 }, (_, i) => this.currentYear - i).reverse();
        // Restaure filtres session (dont plage de dates si déjà choisie), sinon défaut = aujourd'hui
        // (évite un COUNT/scan sur tout l'historique tickets au chargement).
        const restored = this.restoreListFiltersFromSession();
        if (!restored || (!this.dateFilterStart && !this.dateFilterEnd)) {
            this.initDefaultDateFilters();
        } else {
            this.updateSelectedPeriod();
            this.generateWeekDays();
        }
        // Chargement en parallèle : la liste des tickets ne dépend PAS des options d'entrepôt
        // (le filtre entrepôt provient de selectedWarehouseIds restauré depuis la session).
        // On lance donc les deux requêtes en même temps au lieu d'enchaîner tickets APRÈS entrepôts,
        // ce qui accélère nettement l'affichage à l'entrée de la page.
        this.loadTicketsFromApi();
        this.loadWarehouseOptions();
        this.loadEstablishmentOptions();
        this.selectionService.selectedIds$.subscribe((ids) => {
            this.selectedIds = new Set(ids);
            this.cdr.detectChanges();
            this.syncMasterCheckbox();
        });
        this.selectionService.refreshRequested$.subscribe((opts) => {
            // On ne recale sur aujourd'hui que si un filtre date est déjà actif ;
            // en vue « toutes les dates », les nouveaux tickets sont déjà visibles.
            if (opts?.snapDateToToday && (this.dateFilterStart || this.dateFilterEnd)) {
                this.snapDateFilterToToday();
            }
            this.loadTicketsFromApi();
        });
    }

    ngAfterViewInit(): void {
        this.initializeFlatpickr();
        this.refreshFeatherIcons();
        this.syncMasterCheckbox();
    }

    ngOnDestroy(): void {
        this.showFilter = false;
        this.ticketsLoadSub?.unsubscribe();
        this.detailLoadSub?.unsubscribe();
        this.originDetailLoadSub?.unsubscribe();
        this.flatpickrInstance?.destroy();
        this.flatpickrInstance = null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes['status'] && !changes['status'].firstChange) ||
            (changes['listContext'] && !changes['listContext'].firstChange)
        ) {
            this.currentPage = 1;
            this.loadTicketsFromApi();
        }
    }

    get allPageSelected(): boolean {
        if (this.paginatedData.length === 0) {
            return false;
        }
        return this.paginatedData.every((t) => this.selectedIds.has(t.id));
    }

    trackByTicketId(_: number, row: PosTicketRow): string {
        return row.id;
    }

    trackByFilterKey(_: number, f: { key: string }): string {
        return f.key;
    }

    getActiveFilters(): { key: string; label: string; value: string }[] {
        const chips: { key: string; label: string; value: string }[] = [];
        if (this.selectedEstablishmentIds.length) {
            const labels = this.selectedEstablishmentIds.map((id) => {
                const e = this.establishmentOptions.find((x) => x.id === id);
                return e?.label ?? id;
            });
            chips.push({ key: 'establishment', label: 'Établissement', value: labels.join(', ') });
        }
        if (this.selectedWarehouseIds.length) {
            const labels = this.selectedWarehouseIds.map((id) => {
                const w = this.warehouseOptions.find((x) => x.id === id);
                return w?.label ?? id;
            });
            chips.push({ key: 'warehouse', label: 'Entrepôt', value: labels.join(', ') });
        }
        if (this.selectedPaymentTypes.length) {
            chips.push({ key: 'paymentType', label: 'Paiement', value: this.selectedPaymentTypes.join(', ') });
        }
        if (this.showTicketTypeFilter && this.selectedTicketTypes.length) {
            const labels = this.selectedTicketTypes.map(
                (value) => this.ticketTypeFilterOptions.find((option) => option.value === value)?.label ?? value
            );
            chips.push({ key: 'ticketType', label: 'Type de ticket', value: labels.join(', ') });
        }
        if (this.selectedTicketStatuts.length) {
            const labels = this.selectedTicketStatuts.map(
                (v) => this.ticketStatutFilterOptions.find((o) => o.value === v)?.label ?? v
            );
            chips.push({ key: 'ticketStatut', label: 'Statut ticket', value: labels.join(', ') });
        }
        const totalChip = this.formatTotalFilterChip();
        if (totalChip) {
            chips.push({ key: 'total', label: 'Total', value: totalChip });
        }
        return chips;
    }

    private formatTotalFilterChip(): string | null {
        if (!this.totalFilterOperator || this.totalFilterValue == null) {
            return null;
        }
        const v = this.formatMoneyTnd(this.totalFilterValue).replace(' DT', '');
        if (this.totalFilterOperator === 'between') {
            if (this.totalFilterValueMax == null) {
                return null;
            }
            const max = this.formatMoneyTnd(this.totalFilterValueMax).replace(' DT', '');
            return `${v} — ${max}`;
        }
        const sym =
            this.totalFilterOperator === '>'
                ? '>'
                : this.totalFilterOperator === '<'
                  ? '<'
                  : '=';
        return `${sym} ${v} DT`;
    }

    private isTotalFilterActive(): boolean {
        if (!this.totalFilterOperator || this.totalFilterValue == null) {
            return false;
        }
        if (this.totalFilterOperator === 'between') {
            return this.totalFilterValueMax != null;
        }
        return true;
    }

    private matchesTotalFilter(ticketTotal: number): boolean {
        if (!this.isTotalFilterActive()) {
            return true;
        }
        const t = Number(ticketTotal) || 0;
        const v = Number(this.totalFilterValue);
        if (!Number.isFinite(v)) {
            return true;
        }
        switch (this.totalFilterOperator) {
            case '=':
                return Math.abs(t - v) < 0.0005;
            case '>':
                return t > v;
            case '<':
                return t < v;
            case 'between': {
                const max = Number(this.totalFilterValueMax);
                if (!Number.isFinite(max)) {
                    return true;
                }
                const lo = Math.min(v, max);
                const hi = Math.max(v, max);
                return t >= lo && t <= hi;
            }
            default:
                return true;
        }
    }

    toggleFilter(): void {
        this.showFilter = !this.showFilter;
    }

    closeFilterPanel(): void {
        this.showFilter = false;
    }

    resetFilters(): void {
        this.selectedEstablishmentIds = [];
        this.publishSelectedEstablishment();
        this.selectedWarehouseIds = [];
        this.selectedPaymentTypes = [];
        this.selectedTicketTypes = [];
        this.selectedTicketStatuts = [];
        this.totalFilterOperator = '';
        this.totalFilterValue = null;
        this.totalFilterValueMax = null;
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
    }

    applyFilters(): void {
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
        this.closeFilterPanel();
    }

    onSearch(): void {
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
    }

    onPageSizeChange(): void {
        this.currentPage = 1;
        this.persistListFilters();
        this.loadTicketsFromApi();
    }

    onWarehouseFilterChange(): void {
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
    }

    onPaymentTypeFilterChange(): void {
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
    }

    removeFilter(key: string, _event?: Event): void {
        if (key === 'establishment') {
            this.selectedEstablishmentIds = [];
            this.publishSelectedEstablishment();
        }
        if (key === 'warehouse') {
            this.selectedWarehouseIds = [];
        }
        if (key === 'paymentType') {
            this.selectedPaymentTypes = [];
        }
        if (key === 'ticketType') {
            this.selectedTicketTypes = [];
        }
        if (key === 'ticketStatut') {
            this.selectedTicketStatuts = [];
        }
        if (key === 'total') {
            this.totalFilterOperator = '';
            this.totalFilterValue = null;
            this.totalFilterValueMax = null;
        }
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
    }

    updatePagination(): void {
        this.totalPages = this.serverTotalPages || 1;
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        this.applyClientFilters();
    }

    changePage(page: number): void {
        this.currentPage = page;
        this.loadTicketsFromApi();
    }

    toggleSelectAllOnPage(checked: boolean): void {
        for (const t of this.paginatedData) {
            if (checked) {
                this.selectedIds.add(t.id);
            } else {
                this.selectedIds.delete(t.id);
            }
        }
        this.cdr.detectChanges();
        this.syncMasterCheckbox();
        this.pushSelectionToService();
    }

    private pushSelectionToService(): void {
        this.selectionService.setSelectedIds(this.selectedIds);
    }

    toggleTicketSelection(id: string, checked: boolean): void {
        if (checked) {
            this.selectedIds.add(id);
        } else {
            this.selectedIds.delete(id);
        }
        this.cdr.detectChanges();
        this.syncMasterCheckbox();
        this.pushSelectionToService();
    }

    /** État indeterminé de la case « tout » quand seule une partie de la page est cochée. */
    private syncMasterCheckbox(): void {
        queueMicrotask(() => {
            const el = this.selectAllCheckbox?.nativeElement;
            if (!el) {
                return;
            }
            const rows = this.paginatedData;
            if (rows.length === 0) {
                el.indeterminate = false;
                return;
            }
            const selectedCount = rows.filter((r) => this.selectedIds.has(r.id)).length;
            el.indeterminate = selectedCount > 0 && selectedCount < rows.length;
        });
    }

    isTicketSelected(id: string): boolean {
        return this.selectedIds.has(id);
    }

    /** Clic sur la ligne : bascule la case (ignore checkbox, liens, boutons, colonne actions). */
    onTicketRowClick(event: MouseEvent, item: PosTicketRow): void {
        const el = event.target as HTMLElement | null;
        if (!el) {
            return;
        }
        if (el.closest('.pos-ticket-table__td-check') || el.closest('input[type="checkbox"]')) {
            return;
        }
        if (el.closest('a') || el.closest('button')) {
            return;
        }
        if (el.closest('.action-icons')) {
            return;
        }
        this.toggleTicketSelection(item.id, !this.isTicketSelected(item.id));
    }

    formatTicketDate(iso: string): string {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) {
            return '';
        }
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd} / ${mm} / ${yyyy}`;
    }

    paymentStatusLabel(s: PosPaymentStatus): string {
        const m: Record<PosPaymentStatus, string> = {
            paye: 'Payé',
            non_paye: 'Non payé',
            partiel: 'Partiellement payé'
        };
        return m[s];
    }

    getPaymentBadgeNgClass(item: PosTicketRow): Record<string, boolean> {
        const s = item.paymentStatus;
        return {
            badge: true,
            'badge-paye': s === 'paye',
            'badge-nonPaye': s === 'non_paye',
            'badge-partiellement-payé': s === 'partiel'
        };
    }

    displayDocumentTypeLabel(item: PosTicketRow): string {
        if (this.isConsolidatedTicketRow(item)) {
            return 'Consolidé';
        }
        if (item.documentTypeLabel && String(item.documentTypeLabel).trim()) {
            return String(item.documentTypeLabel).trim();
        }
        return formatDocumentTypeLabel(item.documentType);
    }

    /** Statut document = terminé. */
    isDocumentTermine(item: PosTicketRow): boolean {
        return String(item.documentStatutRaw ?? '').trim().toLowerCase() === 'termine';
    }

    /**
     * Tické issu d'un Disponible et clôturé : badge TYPE + icône ✓
     * (signifie que le ticket de vente est terminé).
     */
    isTickeTermine(item: PosTicketRow): boolean {
        const type = String(item.documentType || '').trim();
        const label = this.displayDocumentTypeLabel(item).toLowerCase();
        const isTicke =
            type === 'ticket_vente' || label === 'tické' || label === 'ticke';
        return isTicke && this.isDocumentTermine(item);
    }

    getDocumentTypeBadgeNgClass(item: PosTicketRow): Record<string, boolean> {
        const label = this.displayDocumentTypeLabel(item).toLowerCase();
        return {
            badge: true,
            'badge-ticke': label === 'tické' || label === 'ticke',
            'badge-ferme': label === 'consolidé' || label === 'consolide',
            'badge-en-traitement':
                label === 'disponible' ||
                label === 'en traitement' ||
                label === 'réservation' ||
                label === 'reservation',
            'badge-cree': label === 'facture',
            'badge-negociation': label === 'avoiré' || label === 'avoire',
        };
    }

    get listNumeroColumnLabel(): string {
        if (this.listContext === 'negoce') {
            return 'NUMÉRO DOCUMENT';
        }
        const tab = this.normalizeTabKey();
        if (tab === 'en_traitement' || tab === 'reservation' || tab === 'disponible') {
            return 'RÉF INTERNE';
        }
        return 'NUMÉRO TICKET';
    }

    /** Colonne clôture : listes tickets (hors négoce). */
    get showClotureColumn(): boolean {
        return this.listContext === 'tickets';
    }

    displaySousTypeLabel(item: PosTicketRow): string {
        if (item.sousTypeLabel && String(item.sousTypeLabel).trim()) {
            return String(item.sousTypeLabel).trim();
        }
        return this.displayDocumentTypeLabel(item);
    }

    displayClotureLabel(item: PosTicketRow): string {
        if (item.clotureLabel && String(item.clotureLabel).trim()) {
            return String(item.clotureLabel).trim();
        }
        const raw = String(item.cloture || '').trim().toLowerCase();
        if (raw === 'cloture') {
            return 'Clôturé';
        }
        if (this.isInitialisationTicket(item)) {
            return 'Non clôturé';
        }
        return '—';
    }

    detailPanelTitle(ticket?: PosTicketRow | null): string {
        if (ticket && this.isInitialisationTicket(ticket)) {
            return 'Détail document initialisation';
        }
        return 'Détails ticket de caisse';
    }

    isInitialisationTicket(row: PosTicketRow | null | undefined): boolean {
        if (!row) {
            return false;
        }
        return (
            row.famille === 'initialisation' ||
            String(row.documentType || '').trim() === 'ticket_en_traitement'
        );
    }

    showMontantPaye(vm: PosTicketDetail | null | undefined): boolean {
        if (!vm) {
            return false;
        }
        return (Number(vm.amountPaid) || 0) > 0.0001;
    }

    showResteAPayer(vm: PosTicketDetail | null | undefined): boolean {
        if (!vm) {
            return false;
        }
        const reste = vm.resteAPayer ?? this.resteAPayerOf(vm);
        return (Number(reste) || 0) > 0.0001;
    }

    resteAPayerOf(vm: PosTicketDetail): number {
        return Math.max(
            0,
            Math.round(((Number(vm.totalPrice) || 0) - (Number(vm.amountPaid) || 0)) * 1000) / 1000
        );
    }

    paymentModeLogoUrl(mode: PosPaymentMode | null | undefined): string | null {
        if (!mode) {
            return null;
        }
        const url = mode.logoUrl != null ? String(mode.logoUrl).trim() : '';
        return url || null;
    }

    paymentModeInitial(label: string | null | undefined): string {
        const t = String(label || '').trim();
        return t ? t.charAt(0).toUpperCase() : '?';
    }

    showClotureInitialisationAction(row: PosTicketRow): boolean {
        if (this.listContext !== 'tickets' || !this.usesConsolideLikeTicketActions()) {
            return false;
        }
        if (!this.isInitialisationTicket(row)) {
            return false;
        }
        if (this.isTicketAnnulled(row) || row.hasAnnulationEmis || row.hasVenteEmis) {
            return false;
        }
        return String(row.cloture || '').trim().toLowerCase() !== 'cloture';
    }

    clotureInitialisation(row: PosTicketRow): void {
        if (this.clotureSubmitting || !this.showClotureInitialisationAction(row)) {
            return;
        }
        const documentId = Number(row.id);
        if (!Number.isFinite(documentId) || documentId <= 0) {
            return;
        }
        this.clotureSubmitting = true;
        this.ticketService.updateDocument(documentId, { cloture: 'cloture' }).subscribe({
            next: () => {
                this.clotureSubmitting = false;
                this.toastr.success(
                    `Initialisation ${row.numero} clôturée (sans passage en tické).`,
                    'Clôture'
                );
                if (this.ticketDetail?.ticket?.id === row.id) {
                    this.ticketDetail = {
                        ...this.ticketDetail,
                        ticket: {
                            ...this.ticketDetail.ticket,
                            cloture: 'cloture',
                            clotureLabel: 'Clôturé',
                        },
                    };
                }
                if (this.showPassationModal && this.passationTicket?.id === row.id) {
                    this.closePassationModal();
                }
                this.loadTicketsFromApi();
                this.cdr.detectChanges();
            },
            error: (err: Error) => {
                this.clotureSubmitting = false;
                this.toastr.error(err?.message || 'Clôture impossible.', 'Erreur');
                this.cdr.detectChanges();
            },
        });
    }

    clotureFromPassationModal(): void {
        const row = this.passationTicket;
        if (!row || this.clotureSubmitting) {
            return;
        }
        this.clotureInitialisation(row);
    }

    getDocumentStatutBadgeNgClass(item: PosTicketRow): Record<string, boolean> {
        const raw = String(item.documentStatutRaw ?? '').trim().toLowerCase();
        return {
            badge: true,
            'badge-ticke': raw === 'en_cours',
            'badge-ferme': raw === 'termine',
            'badge-annule': item.documentType === 'ticket_annule',
        };
    }

    displayDocumentStatutLabel(item: PosTicketRow): string {
        if (item.documentStatutLabel && String(item.documentStatutLabel).trim()) {
            return String(item.documentStatutLabel).trim();
        }
        const raw = String(item.documentStatutRaw ?? '').trim();
        if (raw === 'termine') return 'Terminé';
        if (raw === 'en_cours') return 'En cours';
        return raw || '—';
    }

    getTicketBadgeNgClass(item: PosTicketRow): Record<string, boolean> {
        const st = item.ticketStatus;
        return {
            badge: true,
            'badge-en-traitement': st === 'en_traitement',
            'badge-ticke': st === 'ticke',
            'badge-ferme': st === 'consolide',
            'badge-cree': st === 'facture_client',
            'badge-negociation': st === 'avoire',
            'badge-annule': st === 'annule'
        };
    }

    ticketStatusLabel(s: PosTicketAvailability): string {
        const m: Record<PosTicketAvailability, string> = {
            ticke: 'Tické',
            en_traitement: 'Disponible',
            consolide: 'Consolidé',
            facture_client: 'Facture client',
            avoire: 'Avoiré',
            annule: 'Annulé'
        };
        return m[s];
    }

    showTransferToFactureAction(row: PosTicketRow): boolean {
        if (!this.usesConsolideLikeTicketActions() || this.listContext === 'negoce') {
            return false;
        }
        // Transfert → facture uniquement pour les tickets type Tické (pas consolidés / autres).
        if (this.normalizeTabKey() !== 'ticke') {
            return false;
        }
        if (row.ticketStatus === 'facture_client') {
            return false;
        }
        if (row.factureDocumentId != null && row.factureDocumentId > 0) {
            return false;
        }
        if (this.isConsolidatedTicketRow(row)) {
            return false;
        }
        if (row.documentType && row.documentType !== 'ticket_vente') {
            return false;
        }
        if (row.hasAvoireEmis || row.ticketStatus === 'avoire' || row.documentType === 'ticket_avoire') {
            return false;
        }
        return this.isPaidReadyForFacture(row);
    }

    /** Ticket payé / terminé, prêt pour facturation (onglet Tické). */
    private isPaidReadyForFacture(row: PosTicketRow): boolean {
        const docType = String(row.documentType || '').trim();
        if (docType && docType !== 'ticket_vente') {
            return false;
        }
        const statut = String(row.documentStatutRaw ?? '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        return row.paymentStatus === 'paye' || statut === 'termine';
    }

    /** Actions et modal détail alignés sur l’onglet Consolidé. */
    usesConsolideLikeTicketActions(): boolean {
        if (this.consolideLikeActions) {
            return true;
        }
        const tab = this.normalizeTabKey();
        return tab === 'consolide' || tab === 'ticke';
    }

    private isConsolidatedTicketRow(row: PosTicketRow): boolean {
        if (row.isConsolide || row.ticketStatus === 'consolide') {
            return true;
        }
        return false;
    }

    /** Annulation : nouveau document ticket_avoire ou ticket_annule (le source garde son type). */
    showCancelTicketAction(row: PosTicketRow): boolean {
        if (this.listContext !== 'tickets' || this.isTicketAnnulled(row)) {
            return false;
        }
        if (this.normalizeTabKey() === 'avoire' || this.normalizeTabKey() === 'annule') {
            return false;
        }
        const docType = String(row.documentType || '').trim();
        if (
            docType === 'ticket_avoire' ||
            docType === 'ticket_annule' ||
            row.hasAvoireEmis ||
            row.ticketStatus === 'avoire'
        ) {
            return false;
        }
        return docType === 'ticket_vente' || docType === 'ticket_en_traitement';
    }

    /** Passer le ticket vers une caisse connectée (temps réel) — réservations / ventes non payées uniquement. */
    showPassationToCaisseAction(row: PosTicketRow): boolean {
        if (this.listContext !== 'tickets' || !this.usesConsolideLikeTicketActions()) {
            return false;
        }
        if (row.documentType === 'ticket_avoire' || row.documentType === 'ticket_annule') {
            return false;
        }
        if (
            this.isTicketAnnulled(row) ||
            row.hasAvoireEmis ||
            row.ticketStatus === 'avoire'
        ) {
            return false;
        }
        if (row.paymentStatus === 'paye' || this.isPaidReadyForFacture(row)) {
            return false;
        }
        const docType = String(row.documentType || '').trim();
        if (
            docType &&
            docType !== 'ticket_vente' &&
            docType !== 'ticket_en_traitement'
        ) {
            return false;
        }
        const statut = String(row.documentStatutRaw ?? '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        return statut === 'en_cours';
    }

    openPassationModal(row: PosTicketRow): void {
        if (this.passationSubmitting || !this.showPassationToCaisseAction(row)) {
            return;
        }
        this.passationTicket = row;
        this.selectedPassationCaisseId = null;
        this.passationCaisses = [];
        this.passationAmountPaid =
            this.ticketDetail?.ticket?.id === row.id &&
            this.ticketDetail &&
            (Number(this.ticketDetail.amountPaid) || 0) > 0.0001
                ? Number(this.ticketDetail.amountPaid)
                : null;
        this.passationCaissesLoading = true;
        this.showPassationModal = true;
        this.cdr.detectChanges();
        if (this.passationAmountPaid == null && this.isInitialisationTicket(row)) {
            const id = Number(row.id);
            if (Number.isFinite(id) && id > 0) {
                this.ticketService.getTicketDetail(id).subscribe({
                    next: (data) => {
                        if (this.passationTicket?.id !== row.id) {
                            return;
                        }
                        const detail = mapApiDetailToPosDetail(row, data);
                        if ((Number(detail.amountPaid) || 0) > 0.0001) {
                            this.passationAmountPaid = Number(detail.amountPaid);
                            this.cdr.detectChanges();
                        }
                    },
                    error: () => undefined,
                });
            }
        }
        this.caisseService.getCaissesTableRows().subscribe({
            next: (rows) => {
                const entId =
                    row.entrepotId != null && String(row.entrepotId).trim() !== ''
                        ? Number(row.entrepotId)
                        : null;
                const active = (rows || []).filter(
                    (c) => String(c._caisseStatutRaw || '').toLowerCase() === 'active'
                );
                this.passationCaisses = active.filter((c) => {
                    if (entId == null || !Number.isFinite(entId)) {
                        return true;
                    }
                    return Number(c.entrepot_id) === entId;
                });
                if (this.passationCaisses.length === 1) {
                    this.selectedPassationCaisseId = this.passationCaisses[0].id;
                }
                this.passationCaissesLoading = false;
                this.cdr.detectChanges();
                this.refreshFeatherIcons();
            },
            error: (err: Error) => {
                this.passationCaissesLoading = false;
                this.passationCaisses = [];
                this.toastr.error(err.message, 'Caisses');
                this.cdr.detectChanges();
            },
        });
    }

    closePassationModal(): void {
        if (this.passationSubmitting) {
            return;
        }
        this.showPassationModal = false;
        this.passationTicket = null;
        this.passationCaisses = [];
        this.selectedPassationCaisseId = null;
        this.passationCaissesLoading = false;
        this.passationAmountPaid = null;
        this.cdr.detectChanges();
    }

    confirmPassationToCaisse(): void {
        const row = this.passationTicket;
        const caisseId = Number(this.selectedPassationCaisseId);
        const documentId = row ? Number(row.id) : NaN;
        if (!row || !Number.isFinite(documentId) || documentId <= 0) {
            return;
        }
        if (!Number.isFinite(caisseId) || caisseId <= 0) {
            this.toastr.warning('Sélectionnez une caisse.', 'Passation');
            return;
        }
        this.passationSubmitting = true;
        this.ticketService.passationToCaisse(documentId, caisseId).subscribe({
            next: () => {
                this.passationSubmitting = false;
                this.toastr.success(
                    `Ticket ${row.numero} envoyé à la caisse.`,
                    'Passation'
                );
                this.closePassationModal();
                this.loadTicketsFromApi();
            },
            error: (err: Error) => {
                this.passationSubmitting = false;
                this.toastr.error(err?.message || 'Passation impossible.', 'Erreur');
                this.cdr.detectChanges();
            },
        });
    }

    private isTicketAnnulled(row: PosTicketRow): boolean {
        return row.documentType === 'ticket_annule' || row.ticketStatus === 'annule';
    }

    onViewTicket(t: PosTicketRow): void {
        if (this.listContext === 'tickets') {
            this.openTicketDetailPanel(t);
            return;
        }
        if (t.documentType === 'facture') {
            const returnUrl = this.router.url.split('?')[0];
            void this.router.navigate(['/tiers/ticketCaisse/action/facture'], {
                queryParams: {
                    id: t.id,
                    return: returnUrl,
                    edit: t.documentStatutRaw === 'en_cours' ? '1' : undefined,
                },
            });
            return;
        }
        if (this.shouldOpenFactureDetail(t)) {
            this.openFactureDetail(t);
            return;
        }
        this.openTicketDetailPanel(t);
    }

    private shouldOpenFactureDetail(row: PosTicketRow): boolean {
        return (
            this.normalizeTabKey() === 'facture_client' || row.ticketStatus === 'facture_client'
        );
    }

    private openFactureDetail(row: PosTicketRow): void {
        const directId = row.factureDocumentId;
        if (directId != null && directId > 0) {
            this.navigateToFactureDetail(directId);
            return;
        }
        const ticketId = Number(row.id);
        if (!Number.isFinite(ticketId) || ticketId <= 0) {
            this.toastr.error('Identifiant ticket invalide.', 'Facture');
            return;
        }
        this.ticketService.getTicketDetail(ticketId).subscribe({
            next: (data) => {
                const factureId = Number(data['facture_document_id']);
                if (!Number.isFinite(factureId) || factureId <= 0) {
                    this.toastr.error('Aucune facture finalisée liée à ce ticket.', 'Facture');
                    return;
                }
                this.navigateToFactureDetail(factureId);
            },
            error: (err: Error) => {
                this.toastr.error(err.message, 'Facture');
            },
        });
    }

    private navigateToFactureDetail(factureId: number): void {
        const returnUrl = this.router.url.split('?')[0];
        void this.router.navigate(['/tiers/ticketCaisse/action/facture'], {
            queryParams: {
                id: factureId,
                return: returnUrl,
                edit: '1',
            },
        });
    }

    openTicketDetailPanel(row: PosTicketRow): void {
        const id = Number(row.id);
        if (!Number.isFinite(id)) {
            this.toastr.error('Identifiant ticket invalide.', 'Détail');
            return;
        }
        this.closeOriginTicketDetailPanel();
        this.detailLoading = true;
        this.ticketDetail = null;
        this.detailLoadSub?.unsubscribe();
        this.cdr.detectChanges();
        this.detailLoadSub = this.ticketService.getTicketDetail(id).subscribe({
            next: (data) => {
                this.ticketDetail = mapApiDetailToPosDetail(row, data);
                this.detailLoading = false;
                this.cdr.detectChanges();
                this.refreshFeatherIcons();
            },
            error: (err: Error) => {
                this.detailLoading = false;
                this.toastr.error(err.message, 'Détail ticket');
                this.cdr.detectChanges();
            },
        });
    }

    closeTicketDetailPanel(): void {
        this.detailLoadSub?.unsubscribe();
        this.detailLoadSub = null;
        this.ticketDetail = null;
        this.detailLoading = false;
        this.closeOriginTicketDetailPanel();
        this.cdr.detectChanges();
    }

    canOpenOriginTicketDetail(): boolean {
        const id = this.ticketDetail?.ticket.documentOriginalId;
        return id != null && Number.isFinite(Number(id)) && Number(id) > 0;
    }

    openOriginTicketDetailPanel(event?: Event): void {
        event?.stopPropagation();
        if (!this.canOpenOriginTicketDetail()) {
            this.toastr.info('Document d\'origine introuvable.', 'Détail');
            return;
        }
        const origId = Number(this.ticketDetail!.ticket.documentOriginalId);
        if (this.originTicketDetail?.ticket.id === String(origId)) {
            this.closeOriginTicketDetailPanel();
            return;
        }
        this.originDetailLoading = true;
        this.originTicketDetail = null;
        this.originDetailLoadSub?.unsubscribe();
        this.cdr.detectChanges();
        this.originDetailLoadSub = this.ticketService.getTicketDetail(origId).subscribe({
            next: (data) => {
                this.originTicketDetail = mapApiDetailResponseToPosDetail(data);
                this.originDetailLoading = false;
                this.cdr.detectChanges();
                this.refreshFeatherIcons();
            },
            error: (err: Error) => {
                this.originDetailLoading = false;
                this.toastr.error(err.message, 'Ticket origine');
                this.cdr.detectChanges();
            },
        });
    }

    closeOriginTicketDetailPanel(): void {
        this.originDetailLoadSub?.unsubscribe();
        this.originDetailLoadSub = null;
        this.originTicketDetail = null;
        this.originDetailLoading = false;
        this.cdr.detectChanges();
    }

    onClose(): void {
        this.closeTicketDetailPanel();
    }

    onCloseOriginPanel(event?: Event): void {
        event?.stopPropagation();
        this.closeOriginTicketDetailPanel();
    }

    onContentClick(event: Event): void {
        event.stopPropagation();
    }

    formatMoneyTnd(value: number): string {
        return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`;
    }

    trackByLineRef(_: number, line: PosTicketLine): string {
        return `${line.reference}-${line.quantity}-${line.unitPrice}`;
    }

    /** Couleur (ligne principale). */
    colorSizeMain(line: PosTicketLine): string {
        const c = (line.couleur || '').trim();
        return c || '—';
    }

    colorSizeSub(line: PosTicketLine): string {
        return (line.taille || '').trim();
    }

    formatUnitPrice(value: number): string {
        return value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }

    onSyncTicket(t: PosTicketRow): void {
        void t;
    }

    openAnnulerTicketModal(row: PosTicketRow): void {
        const id = Number(row.id);
        if (!Number.isFinite(id)) {
            this.toastr.error('Identifiant ticket invalide.', 'Annulation');
            return;
        }
        if (this.isTicketAnnulled(row)) {
            this.toastr.warning('Ce ticket est déjà annulé.', 'Annulation');
            return;
        }
        this.annulerModalTicketIds = [id];
        this.showAnnulerModal = true;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    closeAnnulerModal(): void {
        if (this.cancelSubmitting) {
            return;
        }
        this.showAnnulerModal = false;
        this.annulerModalTicketIds = [];
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    onConfirmAnnulerTicket(event: { ticketIds: number[]; allowPaidCancel: boolean }): void {
        if (!event.ticketIds.length) {
            return;
        }
        let stationId: number | undefined;
        try {
            stationId = lcStationId(this.authService);
        } catch {
            stationId = this.authService.getBackStationId() ?? undefined;
        }
        this.cancelSubmitting = true;
        this.ticketService
            .cancelTicketsBatch(event.ticketIds, {
                allowPaidCancel: event.allowPaidCancel,
                stationId,
            })
            .subscribe({
                next: (res) => {
                    const n = res.cancelled?.length ?? event.ticketIds.length;
                    this.toastr.success(`${n} ticket(s) annulé(s).`, 'Annulation');
                    this.cancelSubmitting = false;
                    this.showAnnulerModal = false;
                    this.annulerModalTicketIds = [];
                    for (const tid of event.ticketIds) {
                        this.selectedIds.delete(String(tid));
                    }
                    if (
                        this.ticketDetail &&
                        event.ticketIds.includes(Number(this.ticketDetail.ticket.id))
                    ) {
                        this.closeTicketDetailPanel();
                    }
                    this.loadTicketsFromApi();
                    this.cdr.detectChanges();
                },
                error: (err: Error) => {
                    this.toastr.error(err?.message || 'Annulation impossible.', 'Erreur');
                    this.cancelSubmitting = false;
                },
            });
    }

    openConvertModal(row: PosTicketRow): void {
        if (this.transferInProgress || !this.showTransferToFactureAction(row)) {
            return;
        }
        this.ticketToConvert = row;
        // Transfert tické → facture : type consolidation (s'affiche ensuite dans Consolidé).
        this.convertModalDefaultType = 'Facture de consolidation';
        this.showConvertModal = true;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    closeConvertModal(force = false): void {
        if (this.transferInProgress && !force) {
            return;
        }
        this.showConvertModal = false;
        this.ticketToConvert = null;
        this.convertModalDefaultType = null;
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
    }

    onConvertDocument(target: ConvertDocumentTarget): void {
        const ticket = this.ticketToConvert;
        if (!ticket) {
            return;
        }
        if (target !== 'facture' &&
            target !== 'facture_client' &&
            target !== 'facture_consolidation'
        ) {
            this.closeConvertModal();
            this.toastr.info('Ce type de conversion sera disponible prochainement.', 'Tickets');
            return;
        }
        // Marque documents.consolide = 1 pour l'onglet Consolidé (comme le multi-transfert).
        const markConsolide = true;
        this.proceedTransferToFacture(ticket, markConsolide);
    }

    /** Transfert ticket_vente → brouillon facture (comme onglet Consolidé). */
    private proceedTransferToFacture(ticket: PosTicketRow, markConsolide = false): void {
        const ticketId = Number(ticket.id);
        if (!Number.isFinite(ticketId) || ticketId <= 0) {
            this.toastr.error('Identifiant ticket invalide.', 'Transfert');
            return;
        }
        if (this.transferInProgress) {
            return;
        }
        let stationId: number;
        try {
            stationId = lcStationId(this.authService);
        } catch (e) {
            this.toastr.error(
                readObservableError(e, 'Session invalide : reconnectez-vous.'),
                'Transfert'
            );
            return;
        }
        this.transferLoadingMessage = 'Transfert vers facture en cours…';
        this.transferInProgress = true;
        this.cdr.detectChanges();
        this.ticketService
            .createDraftFactureFromTickets([ticketId], stationId, { markConsolide })
            .subscribe({
            next: (res) => {
                this.transferLoadingMessage = 'Ouverture de la facture…';
                this.closeConvertModal(true);
                if (this.ticketDetail?.ticket.id === ticket.id) {
                    this.closeTicketDetailPanel();
                }
                this.selectedIds.delete(ticket.id);
                this.pushSelectionToService();
                const returnUrl = this.router.url.split('?')[0];
                void this.router
                    .navigate(['/tiers/ticketCaisse/action/facture'], {
                        queryParams: {
                            id: res.facture_id,
                            return: returnUrl,
                            edit: '1',
                        },
                    })
                    .then(() => {
                        this.transferInProgress = false;
                        this.cdr.detectChanges();
                    })
                    .catch(() => {
                        this.transferInProgress = false;
                        this.cdr.detectChanges();
                    });
                this.cdr.detectChanges();
            },
            error: (err: unknown) => {
                this.transferInProgress = false;
                this.cdr.detectChanges();
                this.toastr.error(
                    readObservableError(err, 'Transfert vers facture impossible.'),
                    'Erreur'
                );
            },
        });
    }

    selectDay(day: any) {
        this.selectedDay = day.dateString;
        this.selectedDate = this.parseLocalDateKey(day.dateString) || new Date();
        this.selectedMonthIndex = this.selectedDate.getMonth();
        this.selectedYear = this.selectedDate.getFullYear();
        this.dateFilterStart = day.dateString;
        this.dateFilterEnd = day.dateString;
        this.updateSelectedPeriod();
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.setDate([day.dateString, day.dateString], false);
        }
        this.applyDateFilter(day.dateString, day.dateString);
    }

    selectMonth(index: number) {
        this.selectedMonthIndex = index;
        const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
        const monthStart = formatLocalDateKey(new Date(year, index, 1));
        const monthEnd = formatLocalDateKey(new Date(year, index + 1, 0));
        this.dateFilterStart = monthStart;
        this.dateFilterEnd = monthEnd;
        this.selectedPeriod = `Mois: ${this.months[index]} ${year}`;
        this.selectedDate = new Date(year, index, 1);
        this.selectedYear = year;
        this.selectedDay = '';
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
        }
        this.applyDateFilter(monthStart, monthEnd);
    }

    selectYear(year: number) {
        this.selectedYear = year;
        if (this.viewMode === 'annees') {
            const yearStart = `${year}-01-01`;
            const yearEnd = `${year}-12-31`;
            this.dateFilterStart = yearStart;
            this.dateFilterEnd = yearEnd;
            this.selectedPeriod = `Année: ${year}`;
            this.selectedDate = new Date(year, 0, 1);
            this.selectedMonthIndex = null;
            this.selectedDay = '';
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
            }
            this.applyDateFilter(yearStart, yearEnd);
        } else if (this.viewMode === 'mois') {
            const monthIndex = this.selectedMonthIndex !== null ? this.selectedMonthIndex : this.currentMonthIndex;
            const monthStart = formatLocalDateKey(new Date(year, monthIndex, 1));
            const monthEnd = formatLocalDateKey(new Date(year, monthIndex + 1, 0));
            this.dateFilterStart = monthStart;
            this.dateFilterEnd = monthEnd;
            this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
            this.selectedDate = new Date(year, monthIndex, 1);
            this.selectedMonthIndex = monthIndex;
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
            }
            this.applyDateFilter(monthStart, monthEnd);
        } else {
            const yearStart = `${year}-01-01`;
            const yearEnd = `${year}-12-31`;
            this.dateFilterStart = yearStart;
            this.dateFilterEnd = yearEnd;
            this.selectedPeriod = `Année: ${year}`;
            this.applyDateFilter(yearStart, yearEnd);
        }
    }

    incrementDate() {
        if (this.selectedDate) {
            if (this.viewMode === 'mois') {
                this.selectedDate.setMonth(this.selectedDate.getMonth() + 1);
                const year = this.selectedDate.getFullYear();
                const monthIndex = this.selectedDate.getMonth();
                const monthStart = formatLocalDateKey(new Date(year, monthIndex, 1));
                const monthEnd = formatLocalDateKey(new Date(year, monthIndex + 1, 0));
                this.dateFilterStart = monthStart;
                this.dateFilterEnd = monthEnd;
                this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
                this.selectedMonthIndex = monthIndex;
                this.selectedYear = year;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
                }

                this.applyDateFilter(monthStart, monthEnd);
            } else if (this.viewMode === 'annees') {
                this.selectedDate.setFullYear(this.selectedDate.getFullYear() + 1);
                const year = this.selectedDate.getFullYear();
                const yearStart = `${year}-01-01`;
                const yearEnd = `${year}-12-31`;
                this.dateFilterStart = yearStart;
                this.dateFilterEnd = yearEnd;
                this.selectedPeriod = `Année: ${year}`;
                this.selectedYear = year;
                this.selectedMonthIndex = null;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
                }

                this.applyDateFilter(yearStart, yearEnd);
            } else {
                this.selectedDate.setDate(this.selectedDate.getDate() + 1);
                const dayToUse = formatLocalDateKey(this.selectedDate);
                this.selectedDay = dayToUse;
                this.dateFilterStart = dayToUse;
                this.dateFilterEnd = dayToUse;
                this.selectedMonthIndex = this.selectedDate.getMonth();
                this.selectedYear = this.selectedDate.getFullYear();
                this.updateSelectedPeriod();
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([dayToUse, dayToUse], false);
                }

                this.applyDateFilter(dayToUse, dayToUse);
            }
            this.generateWeekDays();
            this.cdr.detectChanges();
        }
    }

    decrementDate() {
        if (this.selectedDate) {
            if (this.viewMode === 'mois') {
                this.selectedDate.setMonth(this.selectedDate.getMonth() - 1);
                const year = this.selectedDate.getFullYear();
                const monthIndex = this.selectedDate.getMonth();
                const monthStart = formatLocalDateKey(new Date(year, monthIndex, 1));
                const monthEnd = formatLocalDateKey(new Date(year, monthIndex + 1, 0));
                this.dateFilterStart = monthStart;
                this.dateFilterEnd = monthEnd;
                this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
                this.selectedMonthIndex = monthIndex;
                this.selectedYear = year;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
                }

                this.applyDateFilter(monthStart, monthEnd);
            } else if (this.viewMode === 'annees') {
                this.selectedDate.setFullYear(this.selectedDate.getFullYear() - 1);
                const year = this.selectedDate.getFullYear();
                const yearStart = `${year}-01-01`;
                const yearEnd = `${year}-12-31`;
                this.dateFilterStart = yearStart;
                this.dateFilterEnd = yearEnd;
                this.selectedPeriod = `Année: ${year}`;
                this.selectedYear = year;
                this.selectedMonthIndex = null;
                this.selectedDay = '';
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
                }

                this.applyDateFilter(yearStart, yearEnd);
            } else {
                this.selectedDate.setDate(this.selectedDate.getDate() - 1);
                const dayToUse = formatLocalDateKey(this.selectedDate);
                this.selectedDay = dayToUse;
                this.dateFilterStart = dayToUse;
                this.dateFilterEnd = dayToUse;
                this.selectedMonthIndex = this.selectedDate.getMonth();
                this.selectedYear = this.selectedDate.getFullYear();
                this.updateSelectedPeriod();
                if (this.datePicker?.nativeElement?._flatpickr) {
                    this.datePicker.nativeElement._flatpickr.setDate([dayToUse, dayToUse], false);
                }

                this.applyDateFilter(dayToUse, dayToUse);
            }
            this.generateWeekDays();
            this.cdr.detectChanges();
        }
    }

    private applyDateFilter(startDate: string, endDate: string): void {
        this.dateFilterStart = startDate;
        this.dateFilterEnd = endDate;
        this.currentPage = 1;
        this.persistListFilters();
        this.recomputeList();
    }

    private initDefaultDateFilters(): void {
        // Défaut = aujourd'hui : limite le volume SQL (COUNT + ORDER) sur l'historique complet.
        // L'utilisateur peut élargir via le calendrier (semaine / mois / année).
        this.snapDateFilterToToday();
    }

    /** Aligne le filtre date sur aujourd’hui (événement temps réel caisse). */
    private snapDateFilterToToday(): void {
        const today = new Date();
        const day = todayLocalDateKey();
        this.selectedDate = today;
        this.selectedDay = day;
        this.selectedMonthIndex = today.getMonth();
        this.selectedYear = today.getFullYear();
        this.dateFilterStart = day;
        this.dateFilterEnd = day;
        this.viewMode = 'jours';
        this.updateSelectedPeriod();
        this.generateWeekDays();
        this.persistListFilters();
        if (this.flatpickrInstance) {
            this.flatpickrInstance.setDate([day, day], false);
        }
    }

    private restoreListFiltersFromSession(): boolean {
        const saved = this.ticketFiltersService.load(this.listContext);
        if (!saved) {
            return false;
        }
        this.viewMode = saved.viewMode;
        this.currentMonthIndex = saved.currentMonthIndex;
        this.currentYear = saved.currentYear;
        this.selectedMonthIndex = saved.selectedMonthIndex;
        this.selectedYear = saved.selectedYear;
        this.selectedDay = saved.selectedDay;
        this.dateFilterStart = saved.dateFilterStart;
        this.dateFilterEnd = saved.dateFilterEnd;
        this.searchTerm = saved.searchTerm;
        this.selectedWarehouseIds = [...saved.selectedWarehouseIds];
        this.selectedPaymentTypes = [...saved.selectedPaymentTypes];
        this.selectedTicketTypes = [...saved.selectedTicketTypes];
        this.selectedTicketStatuts = [...saved.selectedTicketStatuts];
        this.totalFilterOperator = saved.totalFilterOperator ?? '';
        this.totalFilterValue = saved.totalFilterValue;
        this.totalFilterValueMax = saved.totalFilterValueMax;
        this.basicSelectedOption = saved.basicSelectedOption;
        this.showFilter = false;
        if (saved.selectedDay) {
            this.selectedDate = this.parseLocalDateKey(saved.selectedDay) || new Date();
        } else if (saved.dateFilterStart) {
            this.selectedDate = this.parseLocalDateKey(saved.dateFilterStart) || new Date();
        } else {
            this.selectedDate = new Date();
        }
        return true;
    }

    /** Parse YYYY-MM-DD en Date locale (évite le décalage UTC de `new Date('YYYY-MM-DD')`). */
    private parseLocalDateKey(value: string | null | undefined): Date | null {
        const m = String(value ?? '')
            .trim()
            .match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) {
            return null;
        }
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return Number.isNaN(d.getTime()) ? null : d;
    }

    private persistListFilters(): void {
        const state: TicketListFiltersSession = {
            viewMode: this.viewMode,
            currentMonthIndex: this.currentMonthIndex,
            currentYear: this.currentYear,
            selectedMonthIndex: this.selectedMonthIndex,
            selectedYear: this.selectedYear,
            selectedDay: this.selectedDay,
            dateFilterStart: this.dateFilterStart,
            dateFilterEnd: this.dateFilterEnd,
            searchTerm: this.searchTerm,
            selectedWarehouseIds: [...this.selectedWarehouseIds],
            selectedPaymentTypes: [...this.selectedPaymentTypes],
            selectedTicketTypes: [...this.selectedTicketTypes],
            selectedTicketStatuts: [...this.selectedTicketStatuts],
            totalFilterOperator: this.totalFilterOperator,
            totalFilterValue: this.totalFilterValue,
            totalFilterValueMax: this.totalFilterValueMax,
            basicSelectedOption: this.basicSelectedOption,
        };
        this.ticketFiltersService.save(this.listContext, state);
    }

    private formatPeriodJour(d: Date): string {
        return `Jour: ${d.getDate()} ${this.months[d.getMonth()]} ${d.getFullYear()}`;
    }

    updateSelectedPeriod() {
        // Aucun filtre date actif → libellé neutre (tous les tickets).
        if (!this.dateFilterStart && !this.dateFilterEnd) {
            this.selectedPeriod = 'Toutes les dates';
            return;
        }
        const dateToUse = this.selectedDate || new Date();
        const year = dateToUse.getFullYear();
        if (this.viewMode === 'mois' && this.selectedMonthIndex !== null) {
            const selectedYear = this.selectedYear || year;
            this.selectedPeriod = `Mois: ${this.months[this.selectedMonthIndex]} ${selectedYear}`;
        } else if (this.viewMode === 'annees' && this.selectedYear !== null) {
            this.selectedPeriod = `Année: ${this.selectedYear}`;
        } else {
            this.selectedPeriod = this.formatPeriodJour(dateToUse);
        }
    }

    generateWeekDays() {
        const today = new Date();
        const startOfWeek = new Date(this.selectedDate || today);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);

        this.weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            return {
                label: d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
                dateString: formatLocalDateKey(d),
                isToday: d.toDateString() === today.toDateString()
            };
        });
    }

    changeViewMode(mode: 'jours' | 'mois' | 'annees'): void {
        this.viewMode = mode;
        this.centerTodayItem();
        if (mode === 'jours') {
            const dayToUse = this.selectedDay || todayLocalDateKey();
            if (this.dateFilterStart !== dayToUse || this.dateFilterEnd !== dayToUse) {
                this.dateFilterStart = dayToUse;
                this.dateFilterEnd = dayToUse;
                this.selectedDay = dayToUse;
                this.selectedDate = this.parseLocalDateKey(dayToUse) || new Date();
                this.applyDateFilter(dayToUse, dayToUse);
            } else {
                this.selectedDate = this.parseLocalDateKey(dayToUse) || new Date();
            }
            this.updateSelectedPeriod();
        } else if (mode === 'mois') {
            const monthIndex = this.selectedMonthIndex !== null ? this.selectedMonthIndex : this.currentMonthIndex;
            const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
            this.selectedMonthIndex = monthIndex;
            this.selectedYear = year;
            const monthStart = formatLocalDateKey(new Date(year, monthIndex, 1));
            const monthEnd = formatLocalDateKey(new Date(year, monthIndex + 1, 0));
            this.dateFilterStart = monthStart;
            this.dateFilterEnd = monthEnd;
            this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
            this.selectedDate = new Date(year, monthIndex, 1);
            this.selectedDay = '';
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
            }

            this.applyDateFilter(monthStart, monthEnd);
        } else if (mode === 'annees') {
            const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
            this.selectedYear = year;
            this.selectedMonthIndex = null;
            this.selectedDay = '';
            const yearStart = `${year}-01-01`;
            const yearEnd = `${year}-12-31`;
            this.dateFilterStart = yearStart;
            this.dateFilterEnd = yearEnd;
            this.selectedPeriod = `Année: ${year}`;
            this.selectedDate = new Date(year, 0, 1);
            if (this.datePicker?.nativeElement?._flatpickr) {
                this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
            }
            this.applyDateFilter(yearStart, yearEnd);
        }
    }

    centerTodayItem() {
        setTimeout(() => {
            if (this.viewMode === 'jours') {
                const todayElement = document.querySelector('.day-item.today');
                if (todayElement) {
                    todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            } else if (this.viewMode === 'mois') {
                const todayElement = document.querySelector('.month-item.today');
                if (todayElement) {
                    todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            } else if (this.viewMode === 'annees') {
                const todayElement = document.querySelector('.year-item.today');
                if (todayElement) {
                    todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            }
        }, 100);
    }

    openFlatpickr() {
        if (!this.flatpickrInstance && this.datePicker?.nativeElement) {
            this.initializeFlatpickr();
        }
        this.flatpickrInstance?.open();
    }

    private initializeFlatpickr(): void {
        if (!this.datePicker?.nativeElement || this.flatpickrInstance) {
            return;
        }
        this.flatpickrInstance = flatpickr(this.datePicker.nativeElement, {
            locale: French,
            mode: 'range',
            dateFormat: 'Y-m-d',
            defaultDate: [this.dateFilterStart, this.dateFilterEnd],
            appendTo: document.body,
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) {
                    const startDate = this.toIsoDate(selectedDates[0]);
                    const endDate = this.toIsoDate(selectedDates[1]);
                    this.dateFilterStart = startDate;
                    this.dateFilterEnd = endDate;
                    this.selectedDay = startDate === endDate ? startDate : '';
                    this.selectedDate = selectedDates[0];
                    this.selectedMonthIndex = startDate === endDate ? selectedDates[0].getMonth() : null;
                    this.selectedYear = selectedDates[0].getFullYear();
                    if (startDate === endDate) {
                        this.viewMode = 'jours';
                        this.generateWeekDays();
                        this.updateSelectedPeriod();
                    } else {
                        this.selectedPeriod = `${this.formatDateFr(startDate)} - ${this.formatDateFr(endDate)}`;
                    }
                    this.applyDateFilter(startDate, endDate);
                } else if (selectedDates.length === 1) {
                    const single = this.toIsoDate(selectedDates[0]);
                    this.dateFilterStart = single;
                    this.dateFilterEnd = single;
                    this.selectedDay = single;
                    this.selectedDate = selectedDates[0];
                    this.viewMode = 'jours';
                    this.generateWeekDays();
                    this.updateSelectedPeriod();
                    this.applyDateFilter(single, single);
                }
            },
        });
    }

    private toIsoDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatDateFr(iso: string): string {
        return new Date(iso).toLocaleDateString('fr-FR');
    }

    private refreshFeatherIcons(): void {
        setTimeout(() => {
            try {
                Feather.replace();
            } catch {
                /* environnement test / SSR */
            }
            this.cdr.markForCheck();
        }, 0);
    }

    private normalizeDocumentStatutKey(value: string | null | undefined): string {
        const s = String(value ?? '').trim().toLowerCase();
        if (s === 'annule') return 'annulé';
        return s;
    }

    private normalizeTabKey(): string {
        const raw = (this.status || 'Tous')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '_');
        const aliases: Record<string, string> = {
            factureclient: 'facture_client',
            facture_clt: 'facture_client',
            disponible: 'en_traitement',
            reservation: 'en_traitement',
            ticke: 'ticke',
            consolide: 'consolide',
        };
        return aliases[raw] ?? raw;
    }

    private shouldApplySidebarStatutFilter(): boolean {
        return this.listContext === 'tickets' && this.normalizeTabKey() === 'tous';
    }

    private rowMatchesTab(row: PosTicketRow): boolean {
        const tab = this.normalizeTabKey();
        if (tab === 'tous') {
            // Les tickets en traitement restent uniquement dans l'onglet Disponible.
            return String(row.documentType || '').trim() !== 'ticket_en_traitement';
        }
        if (this.listContext === 'negoce') {
            const label = this.displayDocumentTypeLabel(row).toLowerCase();
            if (tab === 'facture') {
                return label === 'facture';
            }
            if (tab === 'avoire') {
                return label === 'avoiré' || label === 'avoire';
            }
            return true;
        }
        if (tab === 'ticke') {
            return this.isTicketVenteListRow(row) && !this.isConsolidatedTicketRow(row);
        }
        if (tab === 'en_traitement' || tab === 'reservation') {
            // Disponible = uniquement En traitement (réf. INT-), jamais un num. tické Magasin.
            return String(row.documentType || '').trim() === 'ticket_en_traitement';
        }
        if (tab === 'consolide') {
            return row.isConsolide === true || row.ticketStatus === 'consolide';
        }
        if (tab === 'avoire') {
            return row.documentType === 'ticket_avoire' || row.ticketStatus === 'avoire';
        }
        if (tab === 'annule') {
            return row.documentType === 'ticket_annule' || row.ticketStatus === 'annule';
        }
        if (tab === 'facture_client') {
            return row.factureDocumentId != null && row.factureDocumentId > 0;
        }
        return row.ticketStatus === tab;
    }

    /** Ligne éligible à l’onglet Tické. */
    private isTicketVenteListRow(row: PosTicketRow): boolean {
        return String(row.documentType || '').trim() === 'ticket_vente';
    }

    /** Filtre « Paiement » : libellé API (Payé…) ou statut normalisé. */
    private rowMatchesPaymentTypeFilter(row: PosTicketRow): boolean {
        const selected = this.selectedPaymentTypes.map((s) =>
            String(s)
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
        );
        if (!selected.length) {
            return true;
        }
        const candidates = [
            row.paymentType,
            this.paymentStatusLabel(row.paymentStatus),
            row.paymentModesLabel,
        ]
            .map((s) =>
                String(s ?? '')
                    .trim()
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
            )
            .filter(Boolean);
        return selected.some((sel) => candidates.some((c) => c === sel || c.includes(sel)));
    }

    private recomputeList(): void {
        this.loadTicketsFromApi();
    }

    private loadTicketsFromApi(): void {
        if (!this.authService.isAuthenticated()) {
            this.ticketsLoadSub?.unsubscribe();
            this.isLoadingTickets = false;
            this.allTickets = [];
            this.serverTotalPages = 1;
            this.serverTotal = 0;
            this.applyClientFilters();
            return;
        }

        this.ticketsLoadSub?.unsubscribe();
        this.isLoadingTickets = true;
        this.listLoading.start();
        this.cdr.detectChanges();
        let entrepotIds = this.computeEffectiveEntrepotIds();
        // Établissement/entrepôt sélectionné mais aucune correspondance → aucun résultat
        // (id impossible plutôt que « pas de filtre »).
        const hasEntrepotConstraint =
            this.selectedWarehouseIds.length > 0 || this.selectedEstablishmentIds.length > 0;
        if (hasEntrepotConstraint && entrepotIds.length === 0) {
            entrepotIds = [-1];
        }
        const tab = this.normalizeTabKey();
        const statut = mapPosTabToApiStatut(tab);
        const attrFilters: {
            statut?: string;
            type?: string;
            sous_types?: string[];
            date_debut?: string;
            date_fin?: string;
            facture_client?: boolean;
            exclude_factured?: boolean;
            document_scope?: 'tickets' | 'negoce';
            negoce_kind?: 'facture' | 'avoire';
            ticket_avoire?: boolean;
            consolide_only?: boolean;
            exclude_consolide?: boolean;
            exclude_en_traitement?: boolean;
            exclude_annule?: boolean;
        } = {
            date_debut: this.dateFilterStart || undefined,
            date_fin: this.dateFilterEnd || undefined,
        };
        if (this.listContext === 'negoce') {
            attrFilters.document_scope = 'negoce';
            attrFilters.type = 'facture';
            if (tab === 'facture') {
                attrFilters.negoce_kind = 'facture';
            } else if (tab === 'avoire') {
                attrFilters.negoce_kind = 'avoire';
            }
        } else if (tab === 'facture_client') {
            attrFilters.facture_client = true;
        } else if (tab === 'ticke') {
            attrFilters.sous_types = this.selectedTicketTypes.length
                ? [...this.selectedTicketTypes]
                : undefined;
            // Famille Tické : ventes et avoirs, sans Initialisation ni Annulé.
            attrFilters.exclude_en_traitement = true;
            attrFilters.exclude_annule = true;
            // Les tickets déjà consolidés restent dans l'onglet Consolidé.
            attrFilters.exclude_consolide = true;
        } else if (tab === 'en_traitement' || tab === 'reservation') {
            attrFilters.type = 'ticket_en_traitement';
        } else if (tab === 'consolide') {
            // Famille Tické consolidée : ventes et avoirs transférés.
            attrFilters.consolide_only = true;
            attrFilters.exclude_en_traitement = true;
            attrFilters.exclude_annule = true;
            attrFilters.sous_types = this.selectedTicketTypes.length
                ? [...this.selectedTicketTypes]
                : undefined;
        } else if (tab === 'avoire') {
            attrFilters.type = 'ticket_avoire';
        } else if (tab === 'annule') {
            attrFilters.type = 'ticket_annule';
        } else if (tab === 'tous') {
            // Onglet Tous : ne pas afficher les tickets en traitement (Disponible).
            attrFilters.exclude_en_traitement = true;
        } else if (statut) {
            attrFilters.statut = statut;
        }

        this.ticketsLoadSub = this.ticketService
            .getTicketsPage({
                actif: 'all',
                page: this.currentPage,
                limit: this.basicSelectedOption,
                entrepotIds: entrepotIds.length ? entrepotIds : undefined,
                attrFilters,
            })
            .subscribe({
                next: (res) => {
                    this.allTickets = res.items.map((row) => mapApiTicketRowToPos(row));
                    this.serverTotalPages = res.pagination.totalPages;
                    this.serverTotal = res.pagination.total;
                    this.isLoadingTickets = false;
                    this.hasDataLoaded = true;
                    this.listLoading.stop();
                    this.applyClientFilters();
                    this.selectionService.requestCountsRefresh();
                },
                error: (err: Error) => {
                    this.isLoadingTickets = false;
                    this.hasDataLoaded = true;
                    this.listLoading.stop();
                    this.toastr.error(err.message, 'Tickets');
                    this.allTickets = [];
                    this.serverTotalPages = 1;
                    this.serverTotal = 0;
                    this.applyClientFilters();
                    this.selectionService.requestCountsRefresh();
                },
            });
    }

    private applyClientFilters(): void {
        const kw = this.searchTerm.trim().toLowerCase();
        this.filteredData = this.allTickets.filter((row) => {
            if (
                this.selectedPaymentTypes.length &&
                !this.rowMatchesPaymentTypeFilter(row)
            ) {
                return false;
            }
            if (
                this.shouldApplySidebarStatutFilter() &&
                this.selectedTicketStatuts.length
            ) {
                const raw = this.normalizeDocumentStatutKey(row.documentStatutRaw);
                if (!this.selectedTicketStatuts.some((s) => this.normalizeDocumentStatutKey(s) === raw)) {
                    return false;
                }
            }
            if (!this.rowMatchesTab(row)) {
                return false;
            }
            if (!this.matchesTotalFilter(row.ticketTotal ?? 0)) {
                return false;
            }
            if (kw) {
                const blob =
                    `${row.numero} ${row.clientName} ${row.clientPhone} ${row.entrepot} ${row.paymentType}`.toLowerCase();
                if (!blob.includes(kw)) {
                    return false;
                }
            }
            return true;
        });
        this.paginatedData = this.filteredData;
        this.totalPages = this.serverTotalPages || 1;
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        this.cdr.detectChanges();
        this.refreshFeatherIcons();
        this.syncMasterCheckbox();
    }

    onEstablishmentFilterChange(): void {
        this.currentPage = 1;
        this.publishSelectedEstablishment();
        this.recomputeList();
    }

    /**
     * Publie l'établissement courant vers le service de sélection
     * (repris lors du transfert des tickets vers une facture).
     * Un seul établissement sélectionné → transmis ; sinon aucun.
     */
    private publishSelectedEstablishment(): void {
        if (this.selectedEstablishmentIds.length === 1) {
            const id = Number(this.selectedEstablishmentIds[0]);
            const opt = this.establishmentOptions.find((o) => o.id === this.selectedEstablishmentIds[0]);
            if (Number.isFinite(id) && id > 0) {
                this.selectionService.setSelectedEstablishment({
                    id,
                    label: opt?.label ?? String(id),
                    code: null,
                });
                return;
            }
        }
        this.selectionService.setSelectedEstablishment(null);
    }

    /** Entrepôts liés aux établissements sélectionnés. */
    private getEntrepotIdsForSelectedEstablishments(): number[] {
        const ids = new Set<number>();
        for (const eid of this.selectedEstablishmentIds) {
            const list = this.entrepotIdsByEtablissement.get(String(eid)) ?? [];
            for (const w of list) {
                const n = Number(w);
                if (Number.isFinite(n) && n > 0) {
                    ids.add(n);
                }
            }
        }
        return Array.from(ids);
    }

    /**
     * Entrepôts effectifs envoyés à l'API :
     * - entrepôt seul : ses ids ; établissement seul : entrepôts liés ;
     * - les deux : intersection.
     */
    private computeEffectiveEntrepotIds(): number[] {
        const warehouseIds = this.selectedWarehouseIds
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n) && n > 0);
        if (this.selectedEstablishmentIds.length === 0) {
            return warehouseIds;
        }
        const establishmentIds = this.getEntrepotIdsForSelectedEstablishments();
        if (warehouseIds.length === 0) {
            return establishmentIds;
        }
        const allowed = new Set(establishmentIds);
        return warehouseIds.filter((id) => allowed.has(id));
    }

    /** Libellé établissement identique à la facture : « libelle (code) ». */
    private etablissementOptionLabel(etab: EtablissementRow): string {
        const libelle = String(etab?.libelle || '').trim();
        const code = String(etab?.code_etablissement || '').trim();
        if (libelle && code) {
            return `${libelle} (${code})`;
        }
        return libelle || code || `Établissement #${etab?.id}`;
    }

    /** Récupération des établissements — même façon que le ng-select de la facture. */
    private fetchEtablissementsRows() {
        if (!this.authService.getBackStationId()) {
            return of([] as EtablissementRow[]);
        }
        try {
            return this.associationsService.listEtablissements().pipe(
                catchError(() => of([] as EtablissementRow[]))
            );
        } catch {
            return of([] as EtablissementRow[]);
        }
    }

    private loadEstablishmentOptions(): void {
        // Liens établissement -> entrepôts (pour le filtrage), en tâche de fond.
        this.associationsService.listEtablissementEntrepotLinks().subscribe({
            next: (links) => {
                const map = new Map<string, string[]>();
                for (const link of links || []) {
                    const key = String(link.etablissement_id);
                    const list = map.get(key) ?? [];
                    list.push(String(link.entrepot_id));
                    map.set(key, list);
                }
                this.entrepotIdsByEtablissement = map;
            },
            error: () => this.entrepotIdsByEtablissement.clear(),
        });

        // Établissements : même logique que la facture (getBackStationId + bootstrap si besoin).
        const applyRows = (rows: EtablissementRow[]) => {
            this.establishmentOptions = (Array.isArray(rows) ? rows : []).map((row) => ({
                id: String(row.id),
                label: this.etablissementOptionLabel(row),
            }));
            this.cdr.detectChanges();
            this.refreshFeatherIcons();
        };

        const load$ = this.authService.isReadyForNavigation()
            ? this.fetchEtablissementsRows()
            : this.authService.bootstrap().pipe(
                  catchError(() => of('needs_setup' as const)),
                  switchMap(() => this.fetchEtablissementsRows())
              );

        load$.subscribe({
            next: applyRows,
            error: () => {
                this.establishmentOptions = [];
            },
        });
    }

    private loadWarehouseOptions(): void {
        // Ne déclenche plus le chargement des tickets : ils sont chargés en parallèle depuis ngOnInit.
        if (!this.authService.isAuthenticated()) {
            this.warehouseOptions = [];
            return;
        }
        this.entrepotService.getEntrepotOptions().subscribe({
            next: (list) => {
                this.warehouseOptions = list;
            },
            error: () => {
                this.warehouseOptions = [];
            },
        });
    }
}
